import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { StatusCard } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { ReferencePicker } from '@/features/traceability/components/pickers/ReferencePicker';
import { countText } from '@/features/traceability/components/pickers/pickerHelpers';
import { useToolSummaries } from '@/features/traceability/hooks/useToolSummaries';
import type { Tool } from '@/features/traceability/type/Tool';
import { toolWriteErrorMessage } from '@/features/traceability/utils/toolForm';
import { findReference, referenceOptions } from '@/features/traceability/utils/toolSummaries';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from '@/hooks/useTranslation';

type ToolReferenceEditorProps = {
  tool: Tool;
  onSaved: (reference: string, created: boolean) => void;
  onCancel: () => void;
};

/**
 * Changes the reference of an existing tool (same write as the creation, without traces: the
 * worker keeps the tool's traces). Opened from the tool page's metadata card.
 */
export function ToolReferenceEditor({ tool, onSaved, onCancel }: ToolReferenceEditorProps) {
  const { t } = useTranslation();
  const http = useApiClient();
  const client = useMemo(() => (http ? new TraceabilityApiClient(http) : null), [http]);
  const { summaries } = useToolSummaries();
  const current = tool.reference?.trim() ?? '';
  const [value, setValue] = useState(current);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const inputId = 'tool-reference-edit';

  useEffect(() => {
    document.getElementById(inputId)?.focus();
    return () => controllerRef.current?.abort();
  }, []);

  const next = value.trim();
  const changed = next !== '' && next !== current;
  const options = useMemo(() => (summaries ? referenceOptions(summaries) : null), [summaries]);
  const isNew = options !== null && changed && findReference(next, options) === null;

  async function save() {
    if (!client || !changed) return;
    controllerRef.current?.abort();
    const ac = new AbortController();
    controllerRef.current = ac;
    setIsSaving(true);
    setError(null);
    try {
      const res = await client.toolwrite(
        {
          tool: { toolId: tool.toolId, reference: next, companyName: tool.companyName, traces: [] },
        },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;
      if (res.ok) onSaved(next, isNew);
      else setError(toolWriteErrorMessage(res, t, 'toolPickers.edit.error'));
    } catch {
      if (!ac.signal.aborted) setError(t('toolPickers.edit.error'));
    } finally {
      if (!ac.signal.aborted) setIsSaving(false);
    }
  }

  return (
    <div
      data-testid="tool-reference-editor"
      className="flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3"
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={inputId}>{t('toolPickers.edit.label', { toolId: tool.toolId })}</Label>
        <ReferencePicker
          id={inputId}
          value={value}
          onChange={setValue}
          summaries={summaries}
          currentReference={current || null}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        <MoveNote current={current} next={changed ? next : null} options={options} isNew={isNew} />
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={save} disabled={!changed} loading={isSaving}>
          {t('toolPickers.edit.save')}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={isSaving}>
          {t('toolPickers.edit.cancel')}
        </Button>
      </div>
      {error && <StatusCard type="error" message={error} />}
    </div>
  );
}

/** Where the tool moves in the tool list; the traces never change. */
function MoveNote({
  current,
  next,
  options,
  isNew,
}: {
  current: string;
  next: string | null;
  options: ReturnType<typeof referenceOptions> | null;
  isNew: boolean;
}) {
  const { t } = useTranslation();
  if (next === null || options === null || !current) return <>{t('toolPickers.edit.note')}</>;
  const left = (findReference(current, options)?.toolCount ?? 1) - 1;
  const to = isNew ? t('toolPickers.edit.newReference', { reference: next }) : next;
  return (
    <>
      {left > 0
        ? t('toolPickers.edit.move', {
            from: current,
            to,
            left: countText(t, 'toolPickers.reference.toolCount', left),
          })
        : t('toolPickers.edit.moveLast', { from: current, to })}
    </>
  );
}
